import { EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { Popconfirm, Space, Tag, Tooltip } from "antd";
import React from "react";
import TableItem from "../../../../Components/TableItem";
import { ButtonItem, InputNumberItem } from "../../../../Components/Items";
import dineroDisabledMask from "../../../../Components/dineroDisabledMask";
import { formatearTiempoCiclo } from "../../PresupuestosPU/OrdenesDeProduccionPU/PlaneacionProduccionPU/planeacion.utils";

const redondear = (valor) => Math.round((Number(valor) || 0) * 10000) / 10000;

const filasDeComponentes = (componentes, clavePadre, cantidadPadre) =>
  (componentes || []).map((componente, indice) => {
    const key = `${clavePadre}-${indice}`;
    const cantidadProyectada = redondear((Number(componente.cantidad) || 0) * cantidadPadre);
    const hijos = filasDeComponentes(componente.componentes, key, cantidadProyectada);
    return {
      key,
      esComponente: true,
      clave: componente.clave,
      descripcion: componente.descripcion,
      matriz: componente.matriz,
      unidad: componente.unidad,
      cantidad: componente.cantidad,
      medida: componente.medida,
      cantidadProyectada,
      costo: componente.costo,
      costoTotal: redondear((Number(componente.costo) || 0) * cantidadProyectada),
      children: hijos.length > 0 ? hijos : undefined,
    };
  });

export const filasDeMatrices = (matrices) =>
  (matrices || []).map((matriz) => {
    const piezas = Number(matriz.piezas) || 0;
    const key = String(matriz.matrizId);
    const hijos = filasDeComponentes(matriz.componentes, key, piezas);
    return {
      key,
      matrizId: matriz.matrizId,
      clave: matriz.clave,
      descripcion: matriz.descripcion,
      autorizado: matriz.autorizado,
      unidad: matriz.unidad,
      cantidad: piezas,
      cantidadProyectada: piezas,
      tiempoCiclo: matriz.tiempoCiclo,
      costo: matriz.costo,
      costoTotal: redondear((Number(matriz.costo) || 0) * piezas),
      children: hijos.length > 0 ? hijos : undefined,
    };
  });

const TablaMatricesPreCotizacion = ({ matrices, soloLectura, unidadTiempoProduccion, onCambiarPiezas, onVer, onQuitar, etiquetaCantidad = "Piezas", expandirTodo = false }) => {
  const columns = [
    {
      title: "Clave",
      dataIndex: "clave",
      key: "clave",
      width: "15%",
      render: (valor) => <span style={{ wordBreak: "break-all" }}>{valor || "—"}</span>,
    },
    {
      title: "Matriz / insumo",
      dataIndex: "descripcion",
      key: "descripcion",
      render: (valor, fila) => (
        <Space size={4} wrap>
          <span style={{ whiteSpace: "normal" }}>{valor}</span>
          {fila.matriz && <Tag color="blue">Matriz</Tag>}
        </Space>
      ),
    },
    { title: "Unidad", dataIndex: "unidad", key: "unidad", width: "6%" },
    {
      title: etiquetaCantidad,
      dataIndex: "cantidad",
      key: "cantidad",
      width: "7%",
      render: (valor, fila) => {
        if (!fila.esComponente && !soloLectura && onCambiarPiezas) {
          return (
            <InputNumberItem value={valor} min={0} style={{ width: "100%" }} onChange={(piezas) => onCambiarPiezas(fila.matrizId, piezas)} />
          );
        }
        return (
          <span>
            {valor}
            {fila.medida !== undefined && fila.medida !== null && <span style={{ color: "#1890ff" }}> · {fila.medida} cm</span>}
          </span>
        );
      },
    },
    {
      title: "Cant. proy.",
      dataIndex: "cantidadProyectada",
      key: "cantidadProyectada",
      width: "8%",
    },
    {
      title: "T. ciclo",
      dataIndex: "tiempoCiclo",
      key: "tiempoCiclo",
      width: "7%",
      render: (valor, fila) => (fila.esComponente ? "" : formatearTiempoCiclo(valor || 0, unidadTiempoProduccion)),
    },
    {
      title: "Costo",
      dataIndex: "costo",
      key: "costo",
      width: "9%",
      align: "right",
      render: (valor) => dineroDisabledMask(valor),
    },
    {
      title: "Costo total",
      dataIndex: "costoTotal",
      key: "costoTotal",
      width: "10%",
      align: "right",
      render: (valor) => dineroDisabledMask(valor),
    },
    ...(onVer || onQuitar
      ? [
          {
            title: "Acción",
            key: "accion",
            width: "7%",
            align: "center",
            render: (_, fila) =>
              fila.esComponente ? null : (
                <Space size={4}>
                  {onVer && (
                    <Tooltip title="Ver matriz">
                      <ButtonItem type="default" size="small" cyan icon={<EyeOutlined />} onClick={() => onVer(fila.matrizId)} />
                    </Tooltip>
                  )}
                  {onQuitar && !soloLectura && (
                    <Popconfirm title="¿Quitar esta matriz de la pre cotización?" onConfirm={() => onQuitar(fila.matrizId)}>
                      <Tooltip title="Quitar">
                        <ButtonItem type="default" size="small" danger icon={<DeleteOutlined />} />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </Space>
              ),
          },
        ]
      : []),
  ];

  const filas = filasDeMatrices(matrices);
  const total = filas.reduce((acc, fila) => acc + (Number(fila.costoTotal) || 0), 0);

  return (
    <TableItem
      size="small"
      rowKey="key"
      pagination={false}
      dataSource={filas}
      columns={columns}
      rowClassName={(fila) => (fila.esComponente ? "fila-bom-pu" : "fila-matriz-pu")}
      expandable={expandirTodo ? { defaultExpandedRowKeys: filas.map((fila) => fila.key) } : undefined}
      locale={{ emptyText: soloLectura ? "Sin matrices" : "Todavía no hay matrices. Agrega una existente o usa \"Generar matriz\"." }}
      summary={() =>
        filas.length > 0 ? (
          <TableItem.Summary.Row>
            <TableItem.Summary.Cell index={0} colSpan={columns.length - (onVer || onQuitar ? 2 : 1)}>
              <b>Total</b>
            </TableItem.Summary.Cell>
            <TableItem.Summary.Cell index={1} align="right">
              <b>{dineroDisabledMask(total)}</b>
            </TableItem.Summary.Cell>
            {(onVer || onQuitar) && <TableItem.Summary.Cell index={2} />}
          </TableItem.Summary.Row>
        ) : null
      }
    />
  );
};

export default TablaMatricesPreCotizacion;
